import { useEffect } from 'react';
import {
  useQuery, useQueryClient, UseQueryResult, useInfiniteQuery, UseInfiniteQueryResult, InfiniteData,
} from '@tanstack/react-query';
import { getVideos } from '../../services/VideoService';
import { VideoResponse } from '../../types/Videos';
import { FeaturesManagement } from '../../../featuresControl';
import { mergeNewestIntoFirstPage } from '../../../utils/queryFilters';

function useGetVideos(fleetId: string, query: string, enabled = true): UseQueryResult<VideoResponse> {
  return useQuery(
    ['videos', fleetId, query],
    async (): Promise<VideoResponse> => getVideos(fleetId, query),
    {
      retry: 1,
      retryDelay: 3000,
      keepPreviousData: true,
      refetchOnWindowFocus: false,
      refetchInterval: FeaturesManagement.productivity.refetchInterval,
      enabled,
    },
  );
}

export function useGetVideosLazy(fleetId: string, payload?: any, query?: string, deps?: any): UseInfiniteQueryResult<VideoResponse> {
  const queryClient = useQueryClient();
  const queryKey = ['videos-lazy', fleetId, deps];

  const infiniteQuery = useInfiniteQuery(
    queryKey,
    async ({ pageParam = query }): Promise<VideoResponse> => getVideos(fleetId, pageParam),
    {
      retry: 1,
      retryDelay: 3000,
      keepPreviousData: true,
      cacheTime: 0,
      refetchOnWindowFocus: false,
      getNextPageParam: (lastPage) => lastPage.nextPage ?? undefined,
    },
  );

  const { data: latestPage } = useQuery(
    ['videos-lazy-latest', fleetId, deps, query],
    async (): Promise<VideoResponse> => getVideos(fleetId, query as string),
    {
      retry: 1,
      retryDelay: 3000,
      refetchOnWindowFocus: false,
      refetchInterval: FeaturesManagement.productivity.refetchIntervalLazyLists,
      enabled: !!infiniteQuery.data,
    },
  );

  useEffect(() => {
    if (!latestPage) return;
    queryClient.setQueryData<InfiniteData<VideoResponse>>(
      queryKey,
      (existing) => mergeNewestIntoFirstPage(existing, latestPage),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [latestPage]);

  return infiniteQuery;
}

export default useGetVideos;
